/**
 * One place that knows how to reach the marketing site, so the app never
 * hard-codes a host for the pages it has to link to but does not own — the
 * AGB, the Datenschutzerklärung, the Impressum.
 *
 * This is the mirror of the site's own `src/config/app.ts`, which resolves
 * links in the other direction through `PUBLIC_APP_URL`. The two builds are
 * deployed as a pair per environment — `next.formalogix.com` beside
 * `app-next.formalogix.com`, `formalogix.com` beside `app.formalogix.com` —
 * and a link that crosses between them has to follow.
 *
 * It used to be two constants pointing at `https://formalogix.com`, which
 * meant the register screen on `-next` sent people to the *old* site's terms
 * to accept terms for the new one.
 *
 * The default is production, so a build that sets nothing behaves as it
 * always did.
 */

const DEFAULT_SITE_URL = 'https://formalogix.com'

function deriveSiteOrigin(): string {
  const explicit = import.meta.env.VITE_SITE_URL
  if (!explicit) return DEFAULT_SITE_URL
  try {
    return new URL(explicit).origin
  } catch {
    // A malformed override is a deployment mistake, not something to render:
    // fall back rather than produce links nobody can follow.
    return DEFAULT_SITE_URL
  }
}

/** The marketing site's origin, e.g. "https://next.formalogix.com". */
export const SITE_URL: string = deriveSiteOrigin()

/**
 * A page on the marketing site. The site is Astro with directory-style URLs,
 * so its pages carry a trailing slash (`/agb/`); linking without one costs a
 * redirect on every click.
 */
export function siteLink(path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`
  const withSlash = clean.endsWith('/') ? clean : `${clean}/`
  return `${SITE_URL}${withSlash}`
}

/** The three pages the app links to by name. */
export const TERMS_URL = siteLink('/agb')
export const PRIVACY_URL = siteLink('/datenschutz')
export const IMPRINT_URL = siteLink('/impressum')
